import { Fragment } from "react";
import { Calendar, Coffee, Clock, Settings2 } from "lucide-react";

export default function CalendarShowcase() {
  return (
    <section
      data-testid="calendar-showcase"
      className="relative py-16 sm:py-24 lg:py-32 border-t border-white/[0.04]"
    >
      <div className="absolute inset-0 radial-glow opacity-40" />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-6">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full glass-card text-[11px] font-semibold uppercase tracking-[0.2em] text-cyan-300">
            <Calendar className="h-3.5 w-3.5" />
            Marcações
          </div>
          <h2 className="mt-6 text-3xl sm:text-4xl md:text-5xl lg:text-[3.5rem] tracking-tighter text-white leading-[1.05]">
            A sua agenda,<br />
            <span className="text-gradient-blue">sempre actualizada.</span>
          </h2>
          <p className="mt-5 sm:mt-6 text-base sm:text-lg text-slate-400 leading-relaxed max-w-xl font-light">
            A IA vê os horários livres no seu Google Calendar em tempo real. Cliente diz
            "segunda às 15h" e ela confirma. Cancelamentos e remarcações pelo
            WhatsApp, sem chamadas.
          </p>

          <div className="mt-10 grid sm:grid-cols-2 gap-4">
            <ConfigCard icon={Clock} title="Duração & buffer" desc="Indica quanto dura cada marcação e o intervalo. Deixamos configurado." />
            <ConfigCard icon={Coffee} title="Pausa para almoço" desc="Diz-nos o horário, a IA respeita-o todos os dias." />
            <ConfigCard icon={Settings2} title="Modo automático ou manual" desc="A IA confirma sozinha ou aguarda a sua aprovação. Escolhe e tratamos do resto." />
            <ConfigCard icon={Calendar} title="Dias disponíveis" desc="Diga-nos quando aceita marcações. Deixamos tudo pronto a funcionar." />
          </div>
        </div>

        <div className="lg:col-span-6">
          <CalendarMockup />
        </div>
      </div>
    </section>
  );
}

function ConfigCard({ icon: Icon, title, desc }) {
  return (
    <div className="glass-card rounded-2xl p-5">
      <div className="h-9 w-9 rounded-lg bg-blue-500/10 border border-blue-500/20 flex items-center justify-center">
        <Icon className="h-4 w-4 text-blue-300" />
      </div>
      <div className="mt-4 text-base font-medium text-white">{title}</div>
      <div className="mt-1.5 text-sm text-slate-400 leading-relaxed">{desc}</div>
    </div>
  );
}

function CalendarMockup() {
  const days = ["Seg", "Ter", "Qua", "Qui", "Sex"];
  const hours = ["09", "10", "11", "12", "13", "14", "15", "16", "17"];
  const events = {
    "Seg-09": { label: "Ana M. · Facial", color: "bg-blue-500/30 border-blue-500/50 text-blue-100" },
    "Seg-11": { label: "Rita P. · Peeling", color: "bg-cyan-500/30 border-cyan-500/50 text-cyan-100" },
    "Ter-10": { label: "Margarida · Spa", color: "bg-blue-500/30 border-blue-500/50 text-blue-100" },
    "Ter-15": { label: "Auto · IA", color: "bg-emerald-500/25 border-emerald-500/50 text-emerald-100" },
    "Qua-09": { label: "Sofia F. · Hidrat.", color: "bg-blue-500/30 border-blue-500/50 text-blue-100" },
    "Qua-13": { label: "Almoço", color: "bg-slate-600/30 border-slate-500/50 text-slate-300", lunch: true },
    "Qui-13": { label: "Almoço", color: "bg-slate-600/30 border-slate-500/50 text-slate-300", lunch: true },
    "Sex-13": { label: "Almoço", color: "bg-slate-600/30 border-slate-500/50 text-slate-300", lunch: true },
    "Seg-13": { label: "Almoço", color: "bg-slate-600/30 border-slate-500/50 text-slate-300", lunch: true },
    "Ter-13": { label: "Almoço", color: "bg-slate-600/30 border-slate-500/50 text-slate-300", lunch: true },
    "Qui-11": { label: "Auto · IA", color: "bg-emerald-500/25 border-emerald-500/50 text-emerald-100" },
    "Sex-16": { label: "Inês L. · Facial", color: "bg-blue-500/30 border-blue-500/50 text-blue-100" },
  };

  return (
    <div className="relative">
      <div className="absolute -inset-6 bg-gradient-to-br from-cyan-500/15 via-blue-500/15 to-transparent blur-3xl rounded-3xl" />
      <div className="relative glass-card rounded-3xl p-5 lg:p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-sm font-semibold text-white">Esta semana</div>
            <div className="text-xs text-slate-400">Google Calendar · sincronizado</div>
          </div>
          <div className="hidden sm:flex items-center gap-3 text-[11px]">
            <Legend color="bg-blue-500/60" label="Manual" />
            <Legend color="bg-emerald-500/60" label="Auto" />
            <Legend color="bg-slate-500/60" label="Almoço" />
          </div>
        </div>

        <div className="grid grid-cols-[28px_repeat(5,1fr)] sm:grid-cols-[40px_repeat(5,1fr)] gap-1 text-[10px] sm:text-[11px]">
          <div />
          {days.map((d) => (
            <div key={d} className="text-center text-slate-400 font-medium pb-2">
              {d}
            </div>
          ))}

          {hours.map((h) => (
            <Fragment key={`row-${h}`}>
              <div className="text-slate-500 font-mono text-[9px] sm:text-[10px] pr-1.5 sm:pr-2 text-right pt-1">
                {h}h
              </div>
              {days.map((d) => {
                const key = `${d}-${h}`;
                const ev = events[key];
                return (
                  <div
                    key={key}
                    className={`h-7 sm:h-9 rounded-md border ${
                      ev
                        ? `${ev.color} px-1 sm:px-2 flex items-center text-[9px] sm:text-[10px] font-medium overflow-hidden whitespace-nowrap`
                        : "border-white/[0.04] bg-white/[0.015]"
                    }`}
                  >
                    {ev?.label}
                  </div>
                );
              })}
            </Fragment>
          ))}
        </div>
      </div>
    </div>
  );
}

function Legend({ color, label }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-slate-400">
      <span className={`h-2 w-2 rounded-sm ${color}`} />
      {label}
    </span>
  );
}
